import type { ExportPreferences } from '@gfe/core/store/user-preferences/user-preferences.model';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  isReferenceCellType,
  referenceUidsOf
} from '@gfe/shared/components/table/table-cell/utils';
import { TableColumn } from '@gfe/shared/components/table/table.model';
import { getCellValue } from '@gfe/shared/utils/table-cell.utils';

import { isExportDurationColumn } from './export-duration-columns.util';

/** A displayed reference column paired with the entity type it points to. */
export interface ExportReferenceColumn<T> {
  column: TableColumn<T>;
  entityType: string;
}

/**
 * The displayed columns that reference another entity: a `uid` / `uid-list` cell
 * carrying a `referenceEntityType`. These are the only columns the export
 * uid/name toggle acts on; every other column is exported verbatim.
 *
 * Locally-overridden tables (`route`, `route_action`) build their columns in
 * code rather than from the CSV transformer schema, so their references live
 * here on the `TableColumn` rather than in the schema that drives the raw
 * transformer export.
 */
export function exportReferenceColumns<T>(
  columns: TableColumn<T>[]
): ExportReferenceColumn<T>[] {
  return columns
    .filter((col) => !isNil(col.referenceEntityType) && isReferenceCellType(col.type))
    .map((col) => ({ column: col, entityType: col.referenceEntityType as string }));
}

/**
 * One entry in the export column plan: an existing table column rendered as-is
 * (`value`, holding the uid or the displayed duration), or a synthetic column
 * holding the reference's resolved name (`name`) / the duration's clock
 * rendering (`duration-hms`).
 */
export type ExportPlanColumn<T> =
  | { kind: 'value'; column: TableColumn<T> }
  | { kind: 'name'; column: TableColumn<T>; entityType: string }
  | { kind: 'duration-hms'; column: TableColumn<T> };

/**
 * Expands the displayed columns into the plan dictated by the export
 * preferences: a reference's uid column is kept only when `includeUid` is on and
 * gains a name column right after it when `includeName` is on; a duration column
 * is kept only when `includeDuration` is on and gains a clock column right after
 * it when `includeDurationHms` is on. Every other column is always kept. With
 * the default prefs (uid and duration on, name and clock off) each column maps
 * to a single `value` entry, so the plan matches the input column order
 * one-to-one and the export stays unchanged.
 */
export function buildTableExportPlan<T>(
  columns: TableColumn<T>[],
  prefs: ExportPreferences
): ExportPlanColumn<T>[] {
  const includeUid = prefs.includeUid !== false;
  const includeName = prefs.includeName === true;
  const includeDuration = prefs.includeDuration !== false;
  const includeHms = prefs.includeDurationHms === true;
  const refKeys = new Set(
    exportReferenceColumns(columns).map((ref) => String(ref.column.key))
  );

  const plan: ExportPlanColumn<T>[] = [];
  for (const column of columns) {
    if (refKeys.has(String(column.key))) {
      if (includeUid) plan.push({ kind: 'value', column });
      if (includeName) {
        plan.push({
          kind: 'name',
          column,
          entityType: column.referenceEntityType as string
        });
      }
      continue;
    }
    if (isExportDurationColumn(column)) {
      if (includeDuration) plan.push({ kind: 'value', column });
      if (includeHms) plan.push({ kind: 'duration-hms', column });
      continue;
    }
    plan.push({ kind: 'value', column });
  }
  return plan;
}

/**
 * The comma-joined resolved names for a reference cell. The cell is read through
 * `getCellValue` so computed columns run their `renderHook` (matching what the
 * table displays), uid lists are flattened, and uids with no resolved name are
 * dropped.
 */
export function referenceNamesCellValue<T>(
  row: T,
  column: TableColumn<T>,
  names: Map<string, string> | undefined
): string {
  return referenceUidsOf(getCellValue(row, column))
    .map((uid) => (typeof uid === 'string' ? (names?.get(uid) ?? '') : ''))
    .filter((name) => name !== '')
    .join(',');
}
